import axios from "axios";
import { decryptAES, encryptAES } from "./encrypt";
import API from "../Endpoint/serviceConstrants";
import { loginAuthApi } from "../../Context/loginAuthApi";
import { attachRetryInterceptor } from "./retryInterceptor";
import { attachUnauthorizedAccessInterceptor } from "./unauthorizedAccess";

let isRefreshing = false;
let refreshSubscribers = [];

const EXCLUDED_REFRESH_URLS = [API.loginAuth, API.clientbyid];

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function normalizeUrl(config) {
  return (config.url || "").replace(config.baseURL || "", "");
}

function getRefreshContext() {
  return {
    partnerCode: api.defaults.headers["partner-code"],
    clientCode: api.defaults.headers["client-code"] || sessionStorage.getItem("clientCode"),
    hasIvKey:
      process.env.REACT_APP_IS_E_E === "false" ||
      !!sessionStorage.getItem("decv"),
  };
}

function shouldRefreshToken() {
  const { partnerCode, clientCode, hasIvKey } = getRefreshContext();
  if (!partnerCode || !clientCode || !hasIvKey) return false;

  const encodedToken = sessionStorage.getItem("authToken");
  const expiryTime = Date.parse(sessionStorage.getItem("expirytime"));

  if (!encodedToken || Number.isNaN(expiryTime)) return true;
  return Date.now() >= expiryTime - 10 * 1000; // refresh 10 sec before expiry
}

async function refreshTokenLogic() {
  const { partnerCode } = getRefreshContext();
  const response = await loginAuthApi({
    partnerCode,
    accessKey: process.env.REACT_APP_E_K,
    secretKey: process.env.REACT_APP_PK,
  });
  const newToken = response.response?.[0]?.token;
  const expiry = new Date(response.response?.[0]?.expiryTime);

  sessionStorage.setItem("expirytime", expiry);
  sessionStorage.setItem("authToken", encryptAES(newToken));

  return newToken;
}

// =====================================================
// CREATE AXIOS INSTANCE
// =====================================================

const api = axios.create({
  baseURL: process.env.REACT_APP_API_Gateway,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// =====================================================
// REQUEST INTERCEPTOR (REFRESH FIRST, THEN SET TOKEN)
// =====================================================

api.interceptors.request.use(
  async (config) => {
    const url = normalizeUrl(config);

    const shouldSkip =
      EXCLUDED_REFRESH_URLS.some((u) => url.startsWith(u)) || config.skipRefresh;

    // ----------------------------
    // CHECK FOR TOKEN EXPIRY FIRST
    // ----------------------------
    if (!shouldSkip && shouldRefreshToken()) {
      if (isRefreshing) {
        // Wait for refresh if already happening
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            config.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(config);
          });
        });
      }

      // Trigger refresh
      isRefreshing = true;

      try {
        const newToken = await refreshTokenLogic();
        isRefreshing = false;

        onTokenRefreshed(newToken); // notify pending requests
      } catch (err) {
        isRefreshing = false;
        refreshSubscribers = [];
        sessionStorage.clear();
        window.location.href = "/";
        throw err;
      }
    }

    // -------------------------------------
    // NOW SET THE UPDATED TOKEN IN HEADERS
    // -------------------------------------
    const encodedToken = sessionStorage.getItem("authToken");
    const token = encodedToken ? decryptAES(encodedToken) : null;

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================================
// ENCRYPTION INTERCEPTORS
// =====================================================

const headersToEncrypt = ["Password", "Userid", "client-code", "partner-code"];
const encryptionEnabled = process.env.REACT_APP_IS_E_E !== "false";

// Helper to always attach createdBy / updatedBy metadata
const addCreatedUpdatedMetadata = (data) => {
  let persistedData = JSON.parse(localStorage.getItem("persist:root") || "{}");
  const loginData = persistedData.loginAuthReducer
    ? JSON.parse(persistedData.loginAuthReducer)
    : null;

  const createdBy = loginData ? loginData?.data?.[0]?.clientId : "1";

  const addMetadata = (item) => ({
    ...item,
    ...(item.id === 0 || !item.id
      ? { createdBy }
      : { updatedBy: createdBy }),
  });

  if (Array.isArray(data)) {
    return data.map((item) =>
      typeof item === "object" && item !== null ? addMetadata(item) : item
    );
  }

  if (typeof data === "object" && data !== null) {
    return addMetadata(data);
  }

  return data;
};

if (encryptionEnabled) {
  api.interceptors.request.use(
    (config) => {
      const isMultipart =
        config.headers["Content-Type"]?.includes("multipart/form-data");
      const encryptionMode = config.headers["Encryption"] || "1";
      config.metadata = { encryptionMode };

      // Encrypt query params
      if (config.params && typeof config.params === "object") {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(config.params))
          if (v != null) sp.append(k, String(v));
        const queryString = sp.toString();
        if (queryString)
          config.params = {
            q: encodeURIComponent(encryptAES(queryString, encryptionMode)),
          };
      }

      // Attach createdBy / updatedBy and then encrypt body
      if (!isMultipart && config.data) {
        config.data = addCreatedUpdatedMetadata(config.data);
        config.data = encryptAES(config.data, encryptionMode);
      }

      // Encrypt selected headers
      headersToEncrypt.forEach((h) => {
        if (config.headers[h])
          config.headers[h] = encryptAES(config.headers[h], encryptionMode);
      });

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Decrypt response
  api.interceptors.response.use(
    (response) => {
      const mode = response.config.metadata?.encryptionMode || "1";

      if (response.headers["enciv"]) {
        sessionStorage.setItem("decv", response.headers["enciv"]);
      }

      if (response.data) response.data = decryptAES(response.data);

      return response;
    },
    (error) => Promise.reject(error)
  );
} else {
  // Even when encryption is disabled, always attach createdBy / updatedBy
  api.interceptors.request.use(
    (config) => {
      const isMultipart =
        config.headers["Content-Type"]?.includes("multipart/form-data");

      if (!isMultipart && config.data) {
        config.data = addCreatedUpdatedMetadata(config.data);
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
}

attachRetryInterceptor(api);
attachUnauthorizedAccessInterceptor(api);

export default api;
